import { ReduxThunkType } from '../../types/ReduxThunkType';

import Booking from '../../types/model/Booking';
import { BookingStatus } from '../../enums/BookingStatus';
import { Actions as BookingActions } from './actions';
import EntityName from '../../enums/EntityName';
import Service from '../../services/BookingService';
import RepositoryFactory from '../../repositories/RepositoryFactory';
import BookingRepository from '../../repositories/BookingRepository';
import { RestErrorResponse } from '../../types/response/RestErrorResponse';
import { BlockedBookingSlotData, BookingStatusUpdateRequest } from './types';
import { convertBinaryArrayToBlob } from '../../utils/convertBinaryArrayToBlob';

const bookingRepository = RepositoryFactory.get(EntityName.Booking) as BookingRepository;

export const blockBookingSlot =
  (data: BlockedBookingSlotData): ReduxThunkType<Promise<string>> =>
  async (dispatch) => {
    try {
      dispatch(BookingActions.blockBookingSlot());
      const response = await Service.blockBookingSlot(data);

      dispatch(BookingActions.blockBookingSlotSuccess(response.data.content));
      if (response.data.result.outputMessage) {
        return Promise.resolve(response.data.result.outputMessage);
      }
    } catch (error) {
      dispatch(BookingActions.blockBookingSlotError(error as RestErrorResponse));
    }
    return Promise.resolve('');
  };

export const cancelActiveBookings =
  (bookingIds: string, type?: string): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(BookingActions.removeBookingSlotsPerDay());
      await Service.cancelActiveBookings(bookingIds, type);
      dispatch(BookingActions.removeBookingSlotsPerDaySuccess());
    } catch (error) {
      dispatch(BookingActions.removeBookingSlotsPerDayError(error as RestErrorResponse));
    }
  };

export const setBookingStatus =
  (booking: Booking, status: BookingStatus, reason?: any, reasonText?: string, isRejected?: boolean): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(BookingActions.setStatus());
      const updatedBooking = await bookingRepository.updateStatus(booking, status, reason, reasonText, isRejected);
      dispatch(BookingActions.setStatusSuccess(updatedBooking));
    } catch (error) {
      dispatch(BookingActions.setStatusError(error as RestErrorResponse));
    }
  };

export const unblockAllDates =
  (projectId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(BookingActions.unblockAllDates());
      await Service.unblockAllDates(projectId);
      dispatch(BookingActions.unblockAllDatesSuccess());
    } catch (error) {
      dispatch(BookingActions.unblockAllDatesError(error as RestErrorResponse));
    }
  };

export const unblockDate =
  (slotIds: string): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(BookingActions.unblockDate());
      await Service.unblockDate(slotIds);
      dispatch(BookingActions.unblockDateSuccess());
    } catch (error) {
      dispatch(BookingActions.unblockDateError(error as RestErrorResponse));
    }
  };

export const getBookingConfirmation =
  (bookingId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(BookingActions.getBookingConfirmation());
      const file: any = await Service.getBookingConfirmation(bookingId);
      const blob = convertBinaryArrayToBlob(file.data);
      saveAs(blob, file.data.fileDownloadName);
      dispatch(BookingActions.getBookingConfirmationSuccess());
    } catch (error) {
      dispatch(BookingActions.getBookingConfirmationError(error as RestErrorResponse));
    }
  };

export const sendBookingEmail =
  (bookingId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(BookingActions.sendBookingEmail());
      await Service.sendBookingEmail(bookingId);
      dispatch(BookingActions.sendBookingEmailSuccess());
    } catch (error) {
      dispatch(BookingActions.sendBookingEmailError(error as RestErrorResponse));
    }
  };

export const editBooking =
  (bookingId: number, data: Partial<Booking>): ReduxThunkType<Promise<string | void>> =>
  async (dispatch) => {
    try {
      dispatch(BookingActions.editBooking());
      const response = await Service.editBookings(bookingId, data);
      if (response.data.result.errorMessage) {
        return response.data.result.errorMessage;
      }
      dispatch(BookingActions.editBookingSuccess(bookingId, data));
    } catch (error) {
      dispatch(BookingActions.editBookingError(error as RestErrorResponse));
    }
    return Promise.resolve();
  };

export const setBookingDoneStatus =
  (booking: Booking, data: BookingStatusUpdateRequest): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(BookingActions.setStatus());
      const updatedBooking = await bookingRepository.updateStatus(booking, data.status, undefined, undefined, false, data.bookingDoneAt);
      dispatch(BookingActions.setStatusSuccess(updatedBooking));
    } catch (error) {
      dispatch(BookingActions.setStatusError(error as RestErrorResponse));
    }
  };

export const editPendingBooking =
  (bookingId: number, data: Partial<Booking>): ReduxThunkType<Promise<string | void>> =>
  async (dispatch) => {
    try {
      dispatch(BookingActions.editBooking());
      const response = await Service.editPendingBooking(bookingId, data);

      if (response.data.result.errorMessage) {
        return response.data.result.errorMessage;
      }

      dispatch(BookingActions.editBookingSuccess(bookingId, data));
      dispatch(BookingActions.closeEditModal());
    } catch (error) {
      dispatch(BookingActions.editBookingError(error as RestErrorResponse));
    }
    return Promise.resolve();
  };
